import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import AppDateInput from '../../components/Common/AppDateInput/AppDateInput';
import billingService from '../../services/billingService';
import { ROLES } from '../../utils/roleUtils';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import '../PracticeManagement/SystemCaregiverManagement/SystemCaregiverManagement.css';
import '../../components/Common/Table/Table.css';
import './BillingDashboard.css';
import './SentBillingReports.css';

const formatWhen = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: String(value), time: '' };
  return {
    date: date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }),
    time: date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    })
  };
};

const sentDay = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const SentBillingReports = () => {
  const user = useSelector((state) => state.auth?.user);
  const isSuperAdmin = Number(user?.role_id) === ROLES.SUPER_ADMIN;
  const { clearBreadcrumbs } = useBreadcrumb();
  const [reports, setReports] = useState([]);
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [practiceId, setPracticeId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await billingService.listSentReports();
        if (!cancelled) setReports(Array.isArray(response?.data) ? response.data : []);
        if (!cancelled && isSuperAdmin) {
          try {
            const practiceResponse = await billingService.getBillingPractices();
            const list = Array.isArray(practiceResponse?.data) ? practiceResponse.data : [];
            setPractices(list
              .map((practice) => ({
                id: String(practice.id),
                name: practice.practice_name || `Practice ${practice.id}`
              }))
              .sort((left, right) => left.name.localeCompare(right.name)));
          } catch (practiceError) {
            console.error('Failed to load practices:', practiceError);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load sent billing reports:', error);
          toast.error(error?.response?.data?.message || 'Failed to load sent reports');
          setReports([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!viewer) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setViewer(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [viewer]);

  const dateRangeInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  const visibleReports = useMemo(() => {
    const query = search.trim().toLowerCase();
    return reports.filter((report) => {
      if (isSuperAdmin && practiceId && String(report.practiceId) !== practiceId) return false;
      if (!dateRangeInvalid) {
        const day = sentDay(report.createdAt);
        if (dateFrom && day && day < dateFrom) return false;
        if (dateTo && day && day > dateTo) return false;
      }
      if (!query) return true;
      return (report.recipients || []).some((recipient) =>
        String(recipient.email || '').toLowerCase().includes(query)
      );
    });
  }, [reports, search, practiceId, dateFrom, dateTo, dateRangeInvalid, isSuperAdmin]);

  const openReport = async (report, mode) => {
    setBusyId(`${mode}-${report.id}`);
    try {
      const response = await billingService.downloadSentReport(report.id, { view: mode === 'view' });
      const url = response?.data?.url;
      if (!url) throw new Error('The PDF link was not available');
      if (mode === 'view') {
        setViewer({
          url,
          title: report.practiceName || 'Billing report',
          detail: [String(report.program || '').toUpperCase(), report.periodLabel].filter(Boolean).join(' · ')
        });
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open sent billing report:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to open the report');
    } finally {
      setBusyId(null);
    }
  };

  const resetFilters = () => {
    setPracticeId('');
    setDateFrom('');
    setDateTo('');
    setSearch('');
  };

  const columnCount = isSuperAdmin ? 8 : 7;

  return (
    <div className="billing-dashboard-feature sent-reports-page">
      <div className="list-screen-page-container scm-page bd-page-root">
        <div className="scm-page-header">
          <h2 className="dashboard-title">Billing Reports</h2>
        </div>
        <div className="scm-list-frame">
          <div className="scm-list-panel">
            <div className="scm-list-body">
      <div className={`scm-header-row sent-reports-filters${isSuperAdmin ? '' : ' is-practice'}`}>
        {isSuperAdmin && (
          <label className="sent-reports-field">
            <span>Practice</span>
            <select value={practiceId} onChange={(event) => setPracticeId(event.target.value)}>
              <option value="">All practices</option>
              {practices.map((practice) => (
                <option key={practice.id} value={practice.id}>{practice.name}</option>
              ))}
            </select>
          </label>
        )}
        <label className="sent-reports-field">
          <span>From</span>
          <AppDateInput
            value={dateFrom}
            max={dateTo || undefined}
            onChange={setDateFrom}
            showCalendarIcon
            placeholder="Start date"
          />
        </label>
        <label className="sent-reports-field">
          <span>To</span>
          <AppDateInput
            value={dateTo}
            min={dateFrom || undefined}
            onChange={setDateTo}
            showCalendarIcon
            placeholder="End date"
          />
        </label>
        <label className="sent-reports-field sent-reports-field-search">
          <span>Email</span>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Recipient email"
            aria-label="Search by recipient email"
          />
        </label>
        <button type="button" className="sent-reports-reset" onClick={resetFilters}>Reset</button>
      </div>
      {dateRangeInvalid && (
        <p className="sent-reports-range-error">The start date cannot be after the end date.</p>
      )}

      <div className="practice-tab-table-card bd-table-container sent-reports-table">
        <div className="reusable-table-container">
        <div className="reusable-table-responsive">
          <table className="reusable-table">
            <colgroup>
              <col className="sent-reports-col-when" />
              {isSuperAdmin && <col className="sent-reports-col-practice" />}
              <col className="sent-reports-col-program" />
              <col className="sent-reports-col-period" />
              <col className="sent-reports-col-recipients" />
              <col className="sent-reports-col-sender" />
              <col className="sent-reports-col-patients" />
              <col className="sent-reports-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th className="th-when">Sent</th>
                {isSuperAdmin && <th className="th-practice">Practice</th>}
                <th className="th-program">Program</th>
                <th className="th-period">Period</th>
                <th className="th-recipients">Recipients</th>
                <th className="th-sender">Sent by</th>
                <th className="th-patients">Patients</th>
                <th className="th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columnCount} className="sent-reports-empty">Loading sent reports…</td>
                </tr>
              )}
              {!loading && visibleReports.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="sent-reports-empty">
                    {reports.length === 0 ? 'No sent billing reports yet.' : 'No reports match these filters.'}
                  </td>
                </tr>
              )}
              {!loading && visibleReports.map((report) => {
                const when = formatWhen(report.createdAt);
                return (
                <tr key={report.id}>
                  <td className="sent-reports-when">
                    {when ? (
                      <>
                        <span>{when.date}</span>
                        {when.time ? <span>{when.time}</span> : null}
                      </>
                    ) : '—'}
                  </td>
                  {isSuperAdmin && <td className="sent-reports-practice-cell">{report.practiceName || '—'}</td>}
                  <td className="sent-reports-program-cell"><span className="sent-reports-program">{String(report.program || '').toUpperCase()}</span></td>
                  <td className="sent-reports-period">
                    <span className="sent-reports-period-text">{report.periodLabel || '—'}</span>
                  </td>
                  <td className="sent-reports-recipients-cell">
                    <div className="sent-reports-recipients">
                      {(report.recipients || []).map((recipient) => (
                        <span key={`${report.id}-${recipient.email}`} title={recipient.name ? `${recipient.name} (${recipient.email})` : recipient.email}>
                          {recipient.email}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="sent-reports-sender" title={report.sentByEmail || report.sentByName || ''}>
                    {report.sentByEmail || report.sentByName || '—'}
                  </td>
                  <td className="sent-reports-patients-cell">{report.patientCount ?? '—'}</td>
                  <td className="sent-reports-actions-cell">
                    <div className="sent-reports-actions">
                      <button
                        type="button"
                        className="sent-reports-view"
                        onClick={() => openReport(report, 'view')}
                        disabled={busyId === `view-${report.id}`}
                      >
                        {busyId === `view-${report.id}` ? 'Opening…' : 'View PDF'}
                      </button>
                      <button
                        type="button"
                        className="sent-reports-download"
                        onClick={() => openReport(report, 'download')}
                        disabled={busyId === `download-${report.id}`}
                      >
                        {busyId === `download-${report.id}` ? 'Opening…' : 'Download'}
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      </div>
            </div>
          </div>
        </div>
      </div>

      {viewer && (
        <div className="sent-reports-viewer" role="presentation" onClick={() => setViewer(null)}>
          <div
            className="sent-reports-viewer-card"
            role="dialog"
            aria-modal="true"
            aria-label="Billing report PDF"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sent-reports-viewer-head">
              <div>
                <h2>{viewer.title}</h2>
                <p>{viewer.detail}</p>
              </div>
              <button type="button" onClick={() => setViewer(null)} aria-label="Close">×</button>
            </div>
            <iframe title="Billing report PDF" src={viewer.url} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SentBillingReports;
